import { gql } from 'graphql-request'

export const equipmentsQuery = gql`
  query Equipments($siteId: String!, $last: Boolean, $siteSessionTypeId: String, $search: String) {
    equipments(siteId: $siteId, siteSessionTypeId: $siteSessionTypeId, search: $search) {
      equipmentId
      siteId
      name
      timerId
      minDelayMinutes
      maxDelayMinutes
      defaultDelayMinutes
      defaultTimerMinutes
      minTimerMinutes
      maxTimerMinutes
      category
      nonUvCategory
      status
      lastAvailableAt
      lastDelayAt
      lastInUseAt
      lastDirtyAt
      lampTrackingEnabled
      lampLifeHours
      lampWarningHours
      facialLampTrackingEnabled
      facialLampLifeHours
      facialLampWarningHours
      currentError {
        errorCode
        errorMessage
        errorAt
      }
      lastError {
        errorCode
        errorMessage
        errorAt
      }
      createdAt
      updatedAt
      manufacturer
      description
      qualityCategory
      siteSessionTypeIds
      image {
        db
        medium
        thumbnail
      }
      queue {
        clientId
        createdAt
      }
      assignments(last: $last) {
        equipmentAssignmentId
        equipmentId
        clientId
        checkInId
        siteSessionTypeId
        delayMinutes
        timerMinutes
        status
        doneAt
        cancelledAt
        reversedAt
        restartedAt
        restartPendingAt
        lampMinutesBeforeRestart
        lampsSwitchedOn
        addOnInvoiceId
        creditLedgerEntryIds
        createdAt
        updatedAt
        client {
          id
          firstName
          lastName
        }
      }
    }
  }
`

export const equipmentQuery = gql`
  query Equipment($equipmentId: String!) {
    equipment(equipmentId: $equipmentId) {
      equipmentId
      siteId
      name
      timerId
      minDelayMinutes
      maxDelayMinutes
      defaultDelayMinutes
      defaultTimerMinutes
      minTimerMinutes
      maxTimerMinutes
      category
      nonUvCategory
      status
      lastAvailableAt
      lastDelayAt
      lastInUseAt
      lastDirtyAt
      lampTrackingEnabled
      lampLifeHours
      lampWarningHours
      facialLampTrackingEnabled
      facialLampLifeHours
      facialLampWarningHours
      currentError {
        errorCode
        errorMessage
        errorAt
      }
      lastError {
        errorCode
        errorMessage
        errorAt
      }
      createdAt
      updatedAt
      manufacturer
      description
      qualityCategory
      siteSessionTypeIds
      image {
        db
        medium
        thumbnail
      }
      queue {
        clientId
        createdAt
      }

      assignments {
        equipmentAssignmentId
        equipmentId
        clientId
        status
        checkInId
        siteSessionTypeId
        createdAt
        doneAt
        cancelledAt
        reversedAt
        restartedAt
        restartPendingAt
        lampMinutesBeforeRestart
        lampsSwitchedOn
        updatedAt
        timerMinutes
        delayMinutes
        startedByStaffName
        isTestTimeOverride
        skinTypeAtStart
        complianceOverrideReason
        regulationOverrides {
          rule
          note
          staffName
          createdAt
        }
      }
    }
  }
`

export const equipmentAssignmentsQuery = gql`
  query EquipmentAssignments($equipmentId: String!, $last: Boolean) {
    equipment(equipmentId: $equipmentId) {
      equipmentId
      assignments(last: $last) {
        equipmentAssignmentId
        equipmentId
        clientId
        checkInId
        delayMinutes
        timerMinutes
        status
        doneAt
        cancelledAt
        restartedAt
        restartPendingAt
        lampMinutesBeforeRestart
        lampsSwitchedOn
        createdAt
        updatedAt
      }
    }
  }
`

export const equipmentAssignmentsBySiteQuery = gql`
  query EquipmentAssignmentsBySite($siteId: String!, $last: Boolean) {
    equipments(siteId: $siteId) {
      equipmentId
      assignments(last: $last) {
        equipmentAssignmentId
        equipmentId
        clientId
        checkInId
        delayMinutes
        timerMinutes
        status
        doneAt
        cancelledAt
        createdAt
        updatedAt
        client {
          id
          firstName
          lastName
        }
      }
    }
  }
`

export const availableEquipmentsQuery = gql`
  query AvailableEquipments($siteId: String!) {
    availableEquipments(siteId: $siteId) {
      equipmentId
      image {
        db
        medium
        thumbnail
      }
    }
  }
`

export const equipmentQueueClientsQuery = gql`
  query EquipmentQueueClients($siteId: String!, $equipmentId: String) {
    equipmentQueueClients(siteId: $siteId, equipmentId: $equipmentId) {
      position
      equipmentId
      clientId
      createdAt
      siteSessionTypeId
    }
  }
`

export const equipmentActivityQuery = gql`
  query EquipmentActivity(
    $siteId: String!
    $startDate: String
    $endDate: String
    $clientId: String
  ) {
    equipmentActivity(
      siteId: $siteId
      startDate: $startDate
      endDate: $endDate
      clientId: $clientId
    ) {
      date
      items {
        equipmentId
        equipmentName
        clientId
        siteSessionTypeId
        durationMinutes
      }
    }
  }
`

export const tanningEligibilityQuery = gql`
  query TanningEligibility(
    $category: EquipmentCategory!
    $clientId: String!
    $siteId: String!
    $equipmentId: ID
  ) {
    tanningEligibility(
      category: $category
      clientId: $clientId
      siteId: $siteId
      equipmentId: $equipmentId
    ) {
      eligible
      nextAllowedAt
      minutesUntilAllowed
      minimumHoursBetweenUses {
        violated
        allowedAt
        minutesUntilAllowed
      }
      hoursBetweenUsesFromStartOfPrevious {
        violated
        allowedAt
        minutesUntilAllowed
      }
      minimumDaysBetweenUses {
        violated
        allowedAt
        minutesUntilAllowed
      }
      customerAgeMaxFrequency {
        violated
        allowedAt
      }
      customerAgeMaxFrequencyAfterNoCategoryTan {
        violated
        allowedAt
      }
      uvAge {
        minimumViolated
        doctorsNoteRequired
        parentalConsentRequired
      }
      nonUvAge {
        minimumViolated
        doctorsNoteRequired
        parentalConsentRequired
      }
      skinType {
        status
        resultLabel
        completedAt
        dueAt
        sourceSubmissionId
        resultSource
        assessedAt
        permittedActions
      }
    }
  }
`

export const equipmentSessionTypeAvailabilityQuery = gql`
  query EquipmentSessionTypeAvailability(
    $siteId: String!
    $equipmentId: String!
    $clientId: String!
  ) {
    equipmentSessionTypeAvailability(
      siteId: $siteId
      equipmentId: $equipmentId
      clientId: $clientId
    ) {
      siteSessionTypeId
      availability
      addOnPrice
      currency
      blockReason
    }
  }
`

export const maintenanceEmailConfiguredQuery = gql`
  query MaintenanceEmailConfigured($siteId: ID!) {
    maintenanceEmailConfigured(siteId: $siteId)
  }
`

export const equipmentDiagnosticsQuery = gql`
  query EquipmentDiagnostics($siteId: String!) {
    equipmentDiagnostics(siteId: $siteId) {
      commPort
      healthy
      connected
      maxRooms
      version
    }
  }
`
